import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Link } from 'react-router-dom';
import cache from 'utilities/cache';
import { useCache } from 'CacheProvider';
import Loading from 'components/Loading';
import { toastr } from 'react-redux-toastr';
import { NEW_VIDEO_UPDATED_EVENT, NOTIFICATION_CATEGORIES } from '../constants/videoNotification';

import { request } from 'utilities/graph';
import { listVideos } from 'graphql/queries';
import { asyncListAll } from 'utilities/graph';
import { sortBy } from 'utilities/sorting';
import { deleteVideo } from 'graphql/mutations';
import InstructionVideoDialog from './InstructionVideoDialog.js';
import TagChip from 'components/TagChip';
import { videoCategories } from '@silvergatedelivery/constants';
import { getVideosByTypeByCreatedAt } from 'graphql/queries';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';

const useStyles = makeStyles((theme) => ({
  mediaContainer: {
    position: 'relative',
  },
  media: {
    height: '70%',
    opacity: 0.1,
  },
  videoContainer: {
    position: 'absolute',
    top: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
  video: {
    objectFit: 'contain',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
  card: {
    height: '100%',
  },
  loadingContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    position: 'relative',
    width: '100%',
  },
  title: {
    fontSize: '2.2rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    color: theme.palette.text.primary,
  },
  cardContent: {
    position: 'relative',
  },
  description: {
    marginBottom: 8,
  },
  updatedAt: {
    fontSize: '0.875rem',
    color: '#666',
    marginBottom: 8,
  },
  addButton: {
    position: 'absolute',
    right: 0,
    top: 0,
  },
  deleteButton: {
    position: 'absolute',
    right: 8,
    top: 12,
  },
  adminControls: {
    display: 'flex',
    height: 20,
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  copyButton: {
    'position': 'absolute',
    'right': 68,
    'top': 12,
  },
  select: {
    position: 'absolute',
    right: 108,
    top: 12,
    fontSize: 'inherit',
  },
}));

const getRelativeTimeString = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 60) return '剛剛';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} 分鐘前`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} 小時前`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} 天前`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `${diffInMonths} 個月前`;
  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} 年前`;
};

export default function InstructionVideoGrids({ category }) {
  const classes = useStyles();
  const { isAdmin, appGroup } = useCache();
  // const history = useHistory();
  const isAdminMode = isAdmin && appGroup === 'Admins';
  const isFacilityMode = appGroup === 'FacilityAdmins';
  const pathPrefix = cache.get('app:pathPrefix');
  const [isLoading, setIsLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [copyPath, setCopyPath] = useState('path');

  const categoryLabel = videoCategories.find((item) => item.value === category)?.label;

  const handleDelete = async (id, title) => {
    const confirmResult = window.confirm(`確定刪除教學影片 ${title} 嗎？`);
    if (!confirmResult) return;

    setIsLoading(true);
    await request(deleteVideo, { input: { id } });

    const matchedIndex = videos.findIndex((video) => video.id === id);
    videos.splice(matchedIndex, 1);

    setVideos([...videos]);
    setIsLoading(false);
  };

  const onUpdate = async (data) => {
    const matchedIndex = videos.findIndex(({ id }) => id === data.id);
    if (matchedIndex >= 0) {
      videos.splice(matchedIndex, 1, data);
    } else {
      videos.unshift(data);
    }
    setVideos([...videos]);

    // Reload videos from server
    const updatedVideos = await asyncListAll(listVideos);
    if (category) {
      const filtered = updatedVideos.filter((item) =>
        item.category === category,
      );
      setVideos(filtered.sort(sortBy('title')));
    } else {
      setVideos(updatedVideos.sort(sortBy('title')));
    }
  };

  const handleCopy = (mediaType, videoUrl, category, id) => {
    const isVideo = mediaType === 'VIDEO';
    const isYoutube = videoUrl.includes('youtube.com');
    const youtubeVideoId = videoUrl.split('embed/')[1];

    let textToCopy;

    if (isVideo && copyPath === 'url') {
      textToCopy = isYoutube ?
        `https://www.youtube.com/watch?v=${youtubeVideoId}` :
        videoUrl;
    } else {
      textToCopy = `${window.location.origin}/facility/instructionVideo/${category}/${id}`;
    }

    if (textToCopy) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          toastr.success('已複製到剪貼簿');
        })
        .catch((err) => {
          toastr.error('複製失敗');
        });
    }
  };


  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const videos = await asyncListAll(listVideos);

      // In Facility mode, store the latest video timestamp in localStorage
      if (isFacilityMode && videos.length > 0) {
        const { data: { getVideosByTypeByCreatedAt: { items: sortedByCreatedAtVideos } } } = await request(getVideosByTypeByCreatedAt, {
          type: 'Video',
          limit: 1,
          sortDirection: 'DESC',
        });
        const latestVideo = sortedByCreatedAtVideos[0];

        // 只有當 category 是 'new' 或 'update' 時才更新快取時間
        const shouldUpdateCache = NOTIFICATION_CATEGORIES.includes(latestVideo.category);

        if (shouldUpdateCache) {
          cache.set('app:facility:notifiedNewestVideoTime', latestVideo.createdAt);
          // 觸發 cache 更新事件
          window.dispatchEvent(new Event(NEW_VIDEO_UPDATED_EVENT));
        }
      }

      if (category) {
        const filtered = videos.filter((item) =>
          item.category === category,
        );
        setVideos(filtered.sort(sortBy('title')));
      } else {
        setVideos(videos.sort(sortBy('title')));
      }
      setIsLoading(false);
    })();
  }, [category]);

  useEffect(() => {
    return () => {
      cache.remove('app:viewedInstructionVideoCategory');
    };
  }, []);

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading fullScreen={false} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="h4" className={classes.title}>{categoryLabel}</Typography>
      <Divider style={{ marginBottom: 20 }} />
      {isAdminMode &&
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'absolute', right: 0, top: 0 }}>
          <span style={{ fontSize: 14, color: '#666' }}>複製網址類型：</span>
          <Select
            value={copyPath}
            onChange={(e) => setCopyPath(e.target.value)}
            displayEmpty
            size={'small'}
            label='複製網址類型'
          >
            <MenuItem value="path">系統網址</MenuItem>
            <MenuItem value="url">影片/文件網址</MenuItem>
          </Select>
          <InstructionVideoDialog
            mode={'add'}
            category={category}
            onUpdate={onUpdate}
          />
        </div>
      }
      <Grid container spacing={2}>
        {videos.length === 0 && <Grid item xs={12}>{'尚無教學媒體'}</Grid>}
        {videos.map(({ id, title, description, videoUrl, thumbnailUrl, tags, mediaType, updatedAt, category }) => (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={id}>
            <Card className={classes.card}>
              <div className={classes.mediaContainer}>
                <CardMedia
                  className={classes.media}
                  image={thumbnailUrl || mediaType === 'PDF' ? PictureAsPdfIcon : '/logos/client_logo_256.png'}
                  title="縮圖"
                  component="img"
                  onError={(e) => {
                    if (e.target.src.includes('client_logo_256.png')) {
                      e.target.onerror = null;
                      return;
                    }
                    e.target.src = '/logos/client_logo_256.png';
                  }}
                />
                <div className={classes.videoContainer}>
                  {mediaType === 'VIDEO' && <Link to={`/${pathPrefix}/instructionVideo/${category}/${id}`}>
                    <video
                      className={classes.video}
                      src={videoUrl}
                      poster={thumbnailUrl || '/logos/client_logo_256.png'}
                      controls
                    />
                  </Link>}
                  {mediaType === 'PDF' &&
                  <Link
                    to={`/${pathPrefix}/instructionVideo/${category}/${id}`}
                    style={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <PictureAsPdfIcon style={{ fontSize: 48 }} />
                  </Link>}
                </div>
              </div>
              <CardContent className={classes.cardContent}>
                {isAdminMode && (
                  <div className={classes.adminControls}>
                    <Tooltip title="複製網址" className={classes.copyButton}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(mediaType, videoUrl, category, id);
                        }}
                        size={'small'}
                      >
                        <FileCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <InstructionVideoDialog
                      mode="edit"
                      buttonStyle={{ position: 'absolute', right: 36, top: 12 }}
                      buttonSize={'small'}
                      videoItem={{ id, title, description, videoUrl, thumbnailUrl, tags, mediaType, category }}
                      onUpdate={onUpdate}
                    />
                    <Tooltip title="刪除教學媒體" className={classes.deleteButton}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(id, title);
                        }}
                        size={'small'}
                        disabled={isLoading}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                )}

                <Typography gutterBottom variant="h5" component="h2">
                  {title}
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p" className={classes.description}>
                  {description}
                </Typography>
                <Typography variant="body2" className={classes.updatedAt}>
                  更新於 {getRelativeTimeString(updatedAt)}
                </Typography>
                <React.Fragment>
                  {tags.items
                    .map(({ tag }) => tag)
                    .sort(sortBy('name'))
                    .sort(sortBy('subcategory'))
                    .sort(sortBy('category'))
                    .map((tag, index)=>(
                      <TagChip
                        key={tag.id}
                        tag={tag}
                        style={{ marginRight: 4, marginBottom: 4 }}
                      />
                    ))}
                </React.Fragment>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

InstructionVideoGrids.propTypes = {
  category: PropTypes.string,
};
